<template>
    <ui-modal
        ref="warningModal"
        centered
        title="Are you still with us?"
        backdrop="static"
        hide-header-close
        :keyboard="false"
    >
        <p>
            We have detected that you have been inactive for
            {{ inactivityMinutes }} minutes. Your session will be closed in:
            <strong>{{ Math.round(countDown) }}s</strong>
        </p>
        <template #footer>
            <ui-button variant="light" @click="logoutUser">
                Logout
            </ui-button>
            <ui-button variant="primary" @click="keepSession">
                Keep session
            </ui-button>
        </template>
    </ui-modal>
</template>

<script>
import UiButton from '@/components/ui/buttons/Button'
import UiModal from '@/components/ui/Modal.vue';
import store from '@/store';

import { currentAppPath, rememberReturnPath } from '@/utils/return-path.util';
import throttle from '@/utils/throttle.util';

const inactivityLimitMs = 15 * 60 * 1000;
const activityStorageKey = 'ecartpay_admin_last_activity';
const activityLogoutKey = 'ecartpay_admin_logout';
const activityChannelName = 'ecartpay_admin_activity';
const activityPublishGapMs = 1000;

export default {
    name: 'AutoLogout',
    components: { UiButton, UiModal },
    data() {
        return {
            events: ['click', 'mousemove', 'mousedown', 'scroll', 'keypress', 'load'],
            refreshTimer: null,
            expiresIn: null,
            secondsToShowModal: 30,
            lastActivity: Date.now(),
            lastPublishedAt: 0,
            countDown: 0,
            modalIsOpen: false,
            isLoggingOut: false,
        };
    },
    computed: {
        inactivityMinutes() {
            return inactivityLimitMs / (60 * 1000);
        },
    },
    mounted() {
        if (!this.hasSession()) {
            return;
        }
        this.setMaxInactivity();
        this.bindActivityChannel();
        this.onStorageActivity = (event) => {
            this.handleStorageActivity(event);
        };
        this.onVisibilityChange = () => {
            this.handleVisibilityChange();
        };
        this.onPageShow = () => {
            this.enforceIdleDeadline();
        };
        window.addEventListener('storage', this.onStorageActivity);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        window.addEventListener('pageshow', this.onPageShow);
        this.addCustomEvents();
        this.bindWarningModal();
        this.recordActivity(Date.now());
        this.setRefreshTimer();
        this.onExternalRefresh = () => {
            if (!this.hasSession()) {
                return;
            }
            this.setMaxInactivity();
            this.closeWarning();
            this.resetTimer();
            this.setRefreshTimer();
        };
        window.addEventListener('admin-session-refreshed', this.onExternalRefresh);
        this.clockTimer = setInterval(() => {
            this.countDown = this.secondsUntilLogout();
            this.enforceIdleDeadline();
        }, 1000);
    },
    beforeUnmount() {
        if (this.onExternalRefresh) {
            window.removeEventListener('admin-session-refreshed', this.onExternalRefresh);
        }
        if (this.onStorageActivity) {
            window.removeEventListener('storage', this.onStorageActivity);
        }
        if (this.onVisibilityChange) {
            document.removeEventListener('visibilitychange', this.onVisibilityChange);
        }
        if (this.onPageShow) {
            window.removeEventListener('pageshow', this.onPageShow);
        }
        this.removeCustomEvents();
        if (this.warningModalEl && this.onWarningShown) {
            this.warningModalEl.removeEventListener('shown.bs.modal', this.onWarningShown);
        }
        clearInterval(this.clockTimer);
        clearTimeout(this.refreshTimer);
        this.activityChannel?.close();
    },
    methods: {
        hasSession() {
            return Boolean(this._getCookie('_exp') && this._getCookie('_tid'));
        },
        expireInSeconds() {
            const value = (this.expiresIn - Date.now()) / 1000;
            return Math.max(value, 0);
        },
        inactivityInSeconds() {
            return (Date.now() - this.lastActivity) / 1000;
        },
        secondsUntilLogout() {
            const logout_at = this.lastActivity + inactivityLimitMs + (this.secondsToShowModal * 1000);
            return Math.max((logout_at - Date.now()) / 1000, 0);
        },
        setMaxInactivity() {
            this.expiresIn = new Date(this._getCookie('_exp')).getTime();
        },
        addCustomEvents() {
            this.activityHandlers = [];
            for (const event_name of this.events) {
                const handler = throttle(() => {
                    this.resetTimer();
                }, 300);
                window.addEventListener(event_name, handler);
                this.activityHandlers.push({ event_name, handler });
            }
        },
        removeCustomEvents() {
            const handlers = this.activityHandlers || [];
            for (const entry of handlers) {
                window.removeEventListener(entry.event_name, entry.handler);
            }
        },
        bindWarningModal() {
            const modal_el = this.$refs.warningModal?.$refs?.modal;
            if (!modal_el) {
                return;
            }
            this.warningModalEl = modal_el;
            this.onWarningShown = () => {
                if (this.modalIsOpen) {
                    return;
                }
                this.$refs.warningModal.hide();
            };
            modal_el.addEventListener('shown.bs.modal', this.onWarningShown);
        },
        bindActivityChannel() {
            if (typeof BroadcastChannel !== 'function') {
                return;
            }
            this.activityChannel = new BroadcastChannel(activityChannelName);
            this.activityChannel.onmessage = (message) => {
                this.handleActivityMessage(message?.data);
            };
        },
        handleActivityMessage(payload) {
            if (!payload) {
                return;
            }
            if (payload.type === 'logout') {
                this.logoutUser();
                return;
            }
            if (payload.type === 'activity') {
                this.applyRemoteActivity(payload.at);
            }
        },
        handleStorageActivity(event) {
            if (event.key === activityLogoutKey) {
                this.logoutUser();
                return;
            }
            if (event.key === activityStorageKey) {
                this.applyRemoteActivity(Number(event.newValue));
            }
        },
        handleVisibilityChange() {
            if (document.visibilityState === 'visible') {
                this.enforceIdleDeadline();
            }
        },
        readStoredActivity() {
            try {
                const stored_activity = Number(localStorage.getItem(activityStorageKey));
                if (Number.isFinite(stored_activity)) {
                    return stored_activity;
                }
                return 0;
            } catch {
                return 0;
            }
        },
        syncSharedActivity() {
            const stored_activity = this.readStoredActivity();
            if (stored_activity > this.lastActivity) {
                this.lastActivity = stored_activity;
            }
        },
        publishActivity(timestamp) {
            if (timestamp - this.lastPublishedAt < activityPublishGapMs) {
                return;
            }
            this.lastPublishedAt = timestamp;
            try {
                localStorage.setItem(activityStorageKey, String(timestamp));
            } catch {
                // Storage can be blocked; the channel still notifies other open tabs.
            }
            this.activityChannel?.postMessage({ type: 'activity', at: timestamp });
        },
        publishLogout() {
            try {
                localStorage.setItem(activityLogoutKey, String(Date.now()));
            } catch {
                // Storage can be blocked; the channel still notifies other open tabs.
            }
            this.activityChannel?.postMessage({ type: 'logout' });
        },
        applyRemoteActivity(timestamp) {
            if (!Number.isFinite(timestamp) || timestamp <= this.lastActivity) {
                return;
            }
            this.lastActivity = timestamp;
            this.enforceIdleDeadline();
        },
        recordActivity(timestamp) {
            this.lastActivity = timestamp;
            this.publishActivity(timestamp);
        },
        enforceIdleDeadline() {
            if (this.isLoggingOut || !this.hasSession()) {
                return;
            }
            this.syncSharedActivity();
            const idle_ms = Date.now() - this.lastActivity;
            const logout_after_ms = inactivityLimitMs + (this.secondsToShowModal * 1000);
            if (idle_ms >= logout_after_ms) {
                this.logoutUser();
                return;
            }
            if (idle_ms >= inactivityLimitMs) {
                this.openWarning();
                return;
            }
            this.closeWarning();
        },
        openWarning() {
            if (this.modalIsOpen) {
                return;
            }
            this.$refs.warningModal.show();
            this.modalIsOpen = true;
        },
        closeWarning() {
            const is_visible = Boolean(this.warningModalEl?.classList.contains('show'));
            if (!this.modalIsOpen && !is_visible) {
                return;
            }
            this.modalIsOpen = false;
            this.$refs.warningModal.hide();
        },
        setRefreshTimer() {
            const seconds = this.expireInSeconds() - this.secondsToShowModal - 5;
            clearTimeout(this.refreshTimer);
            this.refreshTimer = setTimeout(() => {
                const inactivity = this.inactivityInSeconds();
                if (inactivity < seconds - 10) {
                    this.$axios.post('/_/admins/refresh').then(() => {
                        this.setMaxInactivity();
                        this.resetTimer();
                        this.setRefreshTimer();
                    });
                }
            }, seconds * 1000);
        },
        async logoutUser() {
            if (this.isLoggingOut) {
                return;
            }
            this.isLoggingOut = true;
            rememberReturnPath(currentAppPath());
            clearInterval(this.clockTimer);
            this.publishLogout();
            try {
                await store.dispatch('globalFilters/clearAccountFilter');
                await this.$axios.get('/_/logout');
            } catch {
                // Another tab may already have closed the session.
            }
            this._deleteCookie('_tid');
            window.location.href = '/login';
        },
        async keepSession() {
            await this.$axios.patch('/_/admins/refresh');
            this.$refs.warningModal.hide();
            this.modalIsOpen = false;
            this.setMaxInactivity();
            this.resetTimer();
            this.setRefreshTimer();
        },
        resetTimer() {
            if (this.modalIsOpen) {
                return;
            }
            this.recordActivity(Date.now());
        },
    },
};
</script>
